"use client";

import {
  KebabMenuItem,
  TemplateKebabInteractionOption,
  useEmitTemplateKebabInteractionEvent,
} from "@zapier/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  useTemplateActionPermissions,
  useCurrentUser,
  useSplitTreatments,
  type Template,
} from "@zapier/client";
import { getUseTemplateUrl, getEditTemplateUrl } from "@zapier/utils";

import {
  getTemplateDetailsAbsoluteUrl,
  isTemplateOwnedByCurrentUser,
} from "./templateUtils";

interface UseTemplateRowInput {
  template: Template;
  onShareTemplate?: () => void;
  onDeleteTemplate?: () => void;
  onChangeOwnerTemplate?: () => void;
  onDuplicateTemplate?: () => void;
}

/**
 * Builds menu items and label for the template row kebab menu.
 * Actions stay visible and use `disabled` + tooltips when the user lacks access, matching tables/canvases/interfaces.
 */
export function useTemplateRow({
  template,
  onShareTemplate,
  onDeleteTemplate,
  onChangeOwnerTemplate,
  onDuplicateTemplate,
}: UseTemplateRowInput) {
  const t = useTranslations("templates-table");
  const { user } = useCurrentUser();
  const { emit: emitTemplateKebab } = useEmitTemplateKebabInteractionEvent();
  const { isEditorZapTemplatesEditModeEnabled, isDuplicateTemplateEnabled } =
    useSplitTreatments();
  const {
    canUseTemplate,
    canDelete,
    canDuplicate,
    canEditTemplate,
    canOpenTemplateShareModal,
    canChangeOwner,
    hasTeams,
    enablePermissionsCheck,
    isLoading,
    isTemplatePermissionCheckSettled,
  } = useTemplateActionPermissions(template.id, { template });
  const [isUseInProgress, setIsUseInProgress] = useState(false);
  const [pendingUseNavigation, setPendingUseNavigation] = useState(false);
  const [isEditInProgress, setIsEditInProgress] = useState(false);

  /** Same URL as TemplateShareModal "Copy link" — template details page. */
  const handleOpenTemplateDetails = useCallback(() => {
    if (!template.slug || globalThis.window === undefined) return;
    globalThis.window.location.assign(
      getTemplateDetailsAbsoluteUrl(template.slug),
    );
  }, [template.slug]);

  const assignWebIntentUseUrl = useCallback(() => {
    if (!template.slug || globalThis.window === undefined) return;
    setIsUseInProgress(true);
    const referrerUrl = globalThis.window.location.href;
    const url = getUseTemplateUrl(template.slug, { referrerUrl });
    globalThis.window.location.assign(url);
  }, [template.slug]);

  const handleEditTemplate = useCallback(() => {
    if (!template.id || globalThis.window === undefined) return;
    // Emitted before the navigation below, which tears the page down. emitEvent
    // is fire-and-forget, so a share of these events is lost in flight — the
    // same trade-off every navigation-triggered event in this repo makes.
    //
    // Guarded because this is the only emit that gates a navigation: emitEvent
    // synchronously calls the third-party Sprig global and events-toolkit's
    // emit(), and a throw from either would propagate out of this onClick —
    // where no error boundary can catch it — and strand the user on the table.
    // Tracking must never block Edit. The returned promise is caught too, so a
    // rejected emit is logged rather than surfacing as an unhandled rejection.
    try {
      const emitted = emitTemplateKebab(TemplateKebabInteractionOption.Edit, {
        template_id: template.id,
        customuser_id: user?.id,
      });
      void Promise.resolve(emitted).catch((error: unknown) => {
        console.error("Failed to emit template edit tracking event:", error);
      });
    } catch (error) {
      console.error("Failed to emit template edit tracking event:", error);
    }
    setIsEditInProgress(true);
    globalThis.window.location.assign(getEditTemplateUrl(template.id));
  }, [template.id, user?.id, emitTemplateKebab]);

  // Kebab "Use" only: permissions load async; navigate when allowed on same interaction.
  useEffect(() => {
    if (!pendingUseNavigation || !template.slug) return;
    if (!isTemplatePermissionCheckSettled) return;
    setPendingUseNavigation(false);
    if (!canUseTemplate) return;
    assignWebIntentUseUrl();
  }, [
    pendingUseNavigation,
    template.slug,
    isTemplatePermissionCheckSettled,
    canUseTemplate,
    assignWebIntentUseUrl,
  ]);

  const handleUseTemplate = useCallback(() => {
    enablePermissionsCheck();
    if (!template.slug) return;
    if (canUseTemplate && !isLoading) {
      setPendingUseNavigation(false);
      assignWebIntentUseUrl();
      return;
    }
    setPendingUseNavigation(true);
  }, [
    template.slug,
    canUseTemplate,
    isLoading,
    enablePermissionsCheck,
    assignWebIntentUseUrl,
  ]);

  const isTemplateOwner = isTemplateOwnedByCurrentUser(template, user?.id);

  const changeOwnerTooltipText = useMemo(() => {
    if (canChangeOwner || isLoading) {
      return "";
    }
    return hasTeams
      ? t("actions.change-owner-tooltip-no-permission")
      : t("actions.change-owner-tooltip-personal");
  }, [canChangeOwner, hasTeams, isLoading, t]);

  const shareTooltipText = useMemo(() => {
    if (isLoading) {
      return;
    }
    if (!canOpenTemplateShareModal) {
      return isTemplateOwner
        ? t("actions.share-tooltip-no-permission")
        : t("actions.share-tooltip-owner-only");
    }
    return;
  }, [isLoading, canOpenTemplateShareModal, isTemplateOwner, t]);

  const deleteTooltipText = useMemo(() => {
    if (isLoading) {
      return;
    }
    if (!canDelete) {
      return t("actions.delete-tooltip-no-permission");
    }
    return;
  }, [isLoading, canDelete, t]);

  const menuItems = useMemo<KebabMenuItem[]>(() => {
    const useDisabled =
      !template.slug || !canUseTemplate || isLoading || isUseInProgress;

    const shareDisabled = !canOpenTemplateShareModal || isLoading;

    return [
      {
        icon: "arrowBigRight",
        label: isUseInProgress ? t("actions.use-creating") : t("actions.use"),
        onClick: handleUseTemplate,
        disabled: useDisabled,
        loading: isLoading,
        shouldCloseOnSelect: false,
      },
      ...(isEditorZapTemplatesEditModeEnabled && canEditTemplate
        ? [
            {
              icon: "actionEdit" as const,
              label: isEditInProgress
                ? t("actions.edit-loading")
                : t("actions.edit"),
              onClick: handleEditTemplate,
              loading: isEditInProgress,
              shouldCloseOnSelect: false,
            },
          ]
        : []),
      ...(isDuplicateTemplateEnabled && canDuplicate
        ? [
            {
              icon: "actionCopy" as const,
              label: t("actions.duplicate"),
              onClick: () => {
                // The entry point of the duplicate funnel (CRTN-10662). What
                // happens next is emitted by TemplateDuplicateModal as
                // `template_duplicate_modal`, so the click stays distinguishable
                // from the completion. Unlike the Edit option this does not
                // navigate, so the emit needs no teardown guard.
                emitTemplateKebab(TemplateKebabInteractionOption.Duplicate, {
                  template_id: template.id,
                  customuser_id: user?.id,
                });
                onDuplicateTemplate?.();
              },
            },
          ]
        : []),
      {
        icon: "personAdd",
        label: t("actions.share"),
        onClick: () => onShareTemplate?.(),
        disabled: shareDisabled,
        loading: isLoading,
        tooltipText: shareDisabled ? shareTooltipText : undefined,
      },
      {
        icon: "arrowConnect",
        label: t("actions.change-owner"),
        onClick: () => onChangeOwnerTemplate?.(),
        disabled: !canChangeOwner || isLoading,
        loading: isLoading,
        tooltipText: changeOwnerTooltipText,
      },
      { divider: true, label: "divider" },
      {
        icon: "actionTrash",
        label: t("actions.delete"),
        hasWarning: true,
        onClick: () => onDeleteTemplate?.(),
        disabled: !canDelete || isLoading,
        tooltipText: deleteTooltipText,
      },
    ];
  }, [
    t,
    onShareTemplate,
    onDeleteTemplate,
    onChangeOwnerTemplate,
    template.slug,
    canUseTemplate,
    canDelete,
    canOpenTemplateShareModal,
    canChangeOwner,
    isLoading,
    isUseInProgress,
    handleUseTemplate,
    isEditorZapTemplatesEditModeEnabled,
    canEditTemplate,
    isEditInProgress,
    handleEditTemplate,
    isDuplicateTemplateEnabled,
    canDuplicate,
    onDuplicateTemplate,
    template.id,
    user?.id,
    emitTemplateKebab,
    changeOwnerTooltipText,
    shareTooltipText,
    deleteTooltipText,
  ]);

  return {
    menuItems,
    kebabMenuLabel: t("kebab-menu-label"),
    enablePermissionsCheck,
    handleOpenTemplateDetails,
    handleUseTemplate,
    shareMessage: t("share-message"),
  };
}
